import axios from "axios";
import { API_CONFIG, PLATFORM_CONFIG } from "../config/ApiConfig";

/**
 * Access to the new EatProtein platform (nutrition service). The admin
 * login screen signs in here too, with the same username and password: the
 * identity service checks them against the admin system and returns platform
 * tokens (access 15 min, refresh 30 days), kept in localStorage.
 */

const ACCESS_KEY = "platformAccessToken";
const REFRESH_KEY = "platformRefreshToken";
const DEVICE_KEY = "platformDeviceId";
const SIGN_IN_ERROR_KEY = "platformSignInError";
const NAME_KEY = "platformUserName";
const PORTAL_KEY = "platformPortal";

export const PRODUCT_UPDATE = "PRODUCT_UPDATE";
export const NUTRITION_REVIEW = "NUTRITION_REVIEW";
const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN"];

/** Nutrition routes of the PHP API (REACT_APP_NUTRITION_BACKEND=php) instead of the platform services. */
const PHP = PLATFORM_CONFIG.NUTRITION_BACKEND === "php";
export const usesPhpNutrition = () => PHP;

/** Headers every nutrition call needs: the PHP API checks its api-access-key. */
export const nutritionHeaders = () => (PHP ? { "api-access-key": API_CONFIG.ACCESS_KEY } : {});

/** The PHP API wraps bodies as { status, result, message }; the platform sends them bare. */
export const nutritionBody = (body) => (PHP ? body?.result ?? null : body);

/** PHP sign-in: the admin username / password, one 12-hour token and no refresh token. */
async function phpLogin(username, password) {
  const { data } = await axios.post(
    `${PLATFORM_CONFIG.NUTRITION_API}nutrition/auth/login`,
    { username, password },
    { headers: nutritionHeaders() },
  );
  saveTokens({ accessToken: data.result.accessToken, refreshToken: "" });
  return data.result;
}

function deviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = `admin-web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

function saveTokens({ accessToken, refreshToken }) {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function platformSignOut() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(NAME_KEY);
  localStorage.removeItem(PORTAL_KEY);
}

/** Signed in through the nutrition expert login (not the admin login). */
export const isNutritionistSession = () =>
  localStorage.getItem(PORTAL_KEY) === "nutritionist" && !!platformUser();

/** Name shown in the nutrition-expert header. */
export const platformUserName = () => localStorage.getItem(NAME_KEY) ?? "";

/** Why the sign-in at admin login failed, or "" when it worked. */
export const lastSignInError = () => localStorage.getItem(SIGN_IN_ERROR_KEY) ?? "";

/** Decoded access-token claims, or null when signed out. */
export function platformUser() {
  const token = localStorage.getItem(ACCESS_KEY);
  if (!token) return null;
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(payload));
    // The PHP token carries one `role`; the screens read `roles`.
    return claims.roles ? claims : { ...claims, roles: claims.role ? [claims.role] : [] };
  } catch {
    return null;
  }
}

export function hasPermission(permission) {
  return (platformUser()?.permissions ?? []).includes(permission);
}

const isPlatformAdmin = () => (platformUser()?.roles ?? []).some((role) => ADMIN_ROLES.includes(role));

/**
 * Only nutrition experts approve or reject nutrition (and tick "checked against
 * the pack"); admins can view reviews. An admin's older token may still list
 * NUTRITION_REVIEW, so the role decides.
 */
export const canReviewNutrition = () => !isPlatformAdmin() && hasPermission(NUTRITION_REVIEW);

/** Can open the review screens: experts, admins and the catalog team. */
export const canViewNutritionReviews = () => canReviewNutrition() || hasPermission(PRODUCT_UPDATE);

/**
 * Nutrition experts sign in with an email and password kept on the new
 * platform (they have no account in the admin system).
 */
export async function nutritionistLogin(email, password) {
  try {
    if (PHP) {
      // Experts are admin-system users with the NUTRITION_EXPERT role.
      const result = await phpLogin(email.trim(), password);
      localStorage.setItem(PORTAL_KEY, "nutritionist");
      localStorage.setItem(NAME_KEY, result.user?.name || email.trim());
      return platformUser();
    }
    const { data } = await axios.post(`${PLATFORM_CONFIG.IDENTITY_API}v1/auth/nutritionist-login`, {
      email: email.trim(),
      password,
      deviceIdentifier: deviceId(),
      appVersion: "admin-web",
    });
    saveTokens(data);
    localStorage.setItem(PORTAL_KEY, "nutritionist");
    const name = [data.user?.firstName, data.user?.lastName].filter(Boolean).join(" ");
    localStorage.setItem(NAME_KEY, name || data.user?.email || "");
    return platformUser();
  } catch (error) {
    throw new Error(apiMessage(error, "Could not sign in"));
  }
}

/**
 * Signs in to the platform with the admin credentials. Called right after
 * the normal admin login; the nutrition screens never ask for a password.
 */
export async function staffLogin(username, password) {
  localStorage.removeItem(PORTAL_KEY);
  try {
    if (PHP) {
      await phpLogin(username, password);
    } else {
      const { data } = await axios.post(`${PLATFORM_CONFIG.IDENTITY_API}v1/auth/staff-login`, {
        username,
        password,
        deviceIdentifier: deviceId(),
        appVersion: "admin-web",
      });
      saveTokens(data);
    }
    localStorage.removeItem(SIGN_IN_ERROR_KEY);
    return platformUser();
  } catch (error) {
    const message = apiMessage(error, "Could not connect to the nutrition service");
    localStorage.setItem(SIGN_IN_ERROR_KEY, message);
    throw new Error(message);
  }
}

let refreshing = null;

/** New access token from the refresh token; one request at a time. */
async function refreshTokens() {
  if (PHP) {
    // No refresh tokens on the PHP API: signing in again issues a new token.
    platformSignOut();
    throw new Error("Nutrition session ended; sign out and sign in again");
  }
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new Error("Nutrition session ended; sign out and sign in again");

  refreshing =
    refreshing ??
    axios
      .post(`${PLATFORM_CONFIG.IDENTITY_API}v1/auth/refresh`, { refreshToken })
      .then(({ data }) => saveTokens(data))
      .catch((error) => {
        platformSignOut();
        throw new Error(apiMessage(error, "Nutrition session ended; sign out and sign in again"));
      })
      .finally(() => {
        refreshing = null;
      });
  return refreshing;
}

function isExpired() {
  const exp = platformUser()?.exp;
  return !exp || exp * 1000 < Date.now() + 30 * 1000;
}

/**
 * Authenticated call to a platform service; refreshes the access token when
 * it is about to expire. Resolves with the response body, rejects with an
 * Error whose message is safe to show.
 */
export async function platformRequest(baseUrl, method, path, data) {
  if (isExpired()) await refreshTokens();

  const isNutrition = baseUrl === PLATFORM_CONFIG.NUTRITION_API;
  const send = () =>
    axios({
      method,
      url: baseUrl + path,
      data,
      headers: {
        ...(isNutrition ? nutritionHeaders() : {}),
        Authorization: `Bearer ${localStorage.getItem(ACCESS_KEY)}`,
      },
    });
  const body = (response) => (isNutrition ? nutritionBody(response.data) : response.data);

  try {
    return body(await send());
  } catch (error) {
    if (error.response?.status === 401) {
      await refreshTokens();
      try {
        return body(await send());
      } catch (retryError) {
        throw new Error(apiMessage(retryError));
      }
    }
    throw new Error(apiMessage(error));
  }
}

function apiMessage(error, fallback = "Request failed") {
  const message = error.response?.data?.message;
  if (Array.isArray(message)) return message.join(", ");
  if (message) return message;
  if (!error.response) return "Nutrition service is not reachable";
  return fallback;
}
